import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader, Text } from '@vibe/core';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface PickedItem {
  id: string;
  name: string;
  boardId?: string;
  groupName?: string;
}

interface ItemPickerProps {
  /** Currently selected item (null if nothing selected) */
  value: PickedItem | null;
  /** Called when the user picks an item */
  onChange: (item: PickedItem | null) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
}

// ─── GraphQL ──────────────────────────────────────────────────────────────────

const SEARCH_ITEMS_QUERY = `
  query SearchItems($boardId: [ID!]!, $term: String!) {
    boards(ids: $boardId) {
      items_page(limit: 20, query_params: { rules: [{ column_id: "name", compare_value: [$term] }] }) {
        items {
          id
          name
          group { id title }
        }
      }
    }
  }
`;

/**
 * Fallback: Fetch items from all accessible boards (first few boards) and
 * filter client-side. Uses the simpler boards query.
 */
const ALL_BOARDS_ITEMS_QUERY = `
  query SearchAllItems($limit: Int!) {
    boards(limit: 10, order_by: used_at) {
      id
      name
      items_page(limit: $limit) {
        items {
          id
          name
          group { id title }
        }
      }
    }
  }
`;

// ─── Debounce hook ──────────────────────────────────────────────────────────

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ─── Component ──────────────────────────────────────────────────────────────

interface RawItem {
  id: string;
  name: string;
  group?: { id: string; title: string };
}

interface RawBoard {
  id: string;
  name: string;
  items_page: { items: RawItem[] };
}

const ItemPicker: React.FC<ItemPickerProps> = ({
  value,
  onChange,
  placeholder = 'Search for an item…',
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<PickedItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebouncedValue(query, 300);

  // Search items when debounced query changes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setResults([]);
      return;
    }

    let cancelled = false;

    async function search() {
      setIsSearching(true);
      setSearchError(null);
      try {
        const response = await monday.api(ALL_BOARDS_ITEMS_QUERY, {
          variables: { limit: 50 },
        });

        const data = response.data as {
          boards?: RawBoard[];
        };

        if (cancelled) return;

        const lowerQ = debouncedQuery.toLowerCase();
        const items: PickedItem[] = [];

        for (const board of data.boards ?? []) {
          for (const item of board.items_page.items) {
            if (item.name.toLowerCase().includes(lowerQ)) {
              items.push({
                id: item.id,
                name: item.name,
                boardId: board.id,
                groupName: item.group?.title,
              });
            }
          }
          if (items.length >= 20) break;
        }

        setResults(items.slice(0, 20));
      } catch (err) {
        if (!cancelled) {
          setSearchError(
            err instanceof Error ? err.message : 'Search failed',
          );
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }

    search();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputFocus = useCallback(() => {
    if (!disabled) setIsOpen(true);
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      if (!isOpen) setIsOpen(true);
    },
    [isOpen],
  );

  const handleSelect = useCallback(
    (item: PickedItem) => {
      onChange(item);
      setQuery('');
      setIsOpen(false);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setQuery('');
    inputRef.current?.focus();
  }, [onChange]);

  return (
    <div className="rp-item-picker" ref={containerRef}>
      {/* Selected item display */}
      {value && !isOpen ? (
        <div className="rp-item-picker__selected">
          <div className="rp-item-picker__selected-info">
            <span className="rp-item-picker__selected-name">{value.name}</span>
            {value.groupName && (
              <span className="rp-item-picker__selected-group">
                {value.groupName}
              </span>
            )}
          </div>
          {!disabled && (
            <button
              className="rp-item-picker__clear"
              onClick={handleClear}
              type="button"
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <input
          ref={inputRef}
          className="rp-item-picker__input"
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={value ? value.name : placeholder}
          disabled={disabled}
          autoComplete="off"
        />
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="rp-item-picker__dropdown">
          {isSearching && (
            <div className="rp-item-picker__dropdown-status">
              <Loader size={Loader.sizes.XS} />
              <span>Searching…</span>
            </div>
          )}

          {searchError && (
            <div className="rp-item-picker__dropdown-status rp-item-picker__dropdown-status--error">
              <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
                {searchError}
              </Text>
            </div>
          )}

          {!isSearching && !searchError && debouncedQuery.length >= 2 && results.length === 0 && (
            <div className="rp-item-picker__dropdown-status">
              <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                No items found
              </Text>
            </div>
          )}

          {!isSearching && debouncedQuery.length < 2 && (
            <div className="rp-item-picker__dropdown-status">
              <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                Type at least 2 characters to search
              </Text>
            </div>
          )}

          {results.map((item) => (
            <button
              key={`${item.boardId}-${item.id}`}
              className="rp-item-picker__option"
              onClick={() => handleSelect(item)}
              type="button"
            >
              <span className="rp-item-picker__option-name">{item.name}</span>
              {item.groupName && (
                <span className="rp-item-picker__option-group">
                  {item.groupName}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ItemPicker;
